// frontend/src/services/api.js
// Centralized API service for all backend communications

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
  getCheckedOutTools: () => api.get('/dashboard/checked-out-tools'),
  getCalibrationDue: () => api.get('/dashboard/calibration-due'),
  getRecentActivity: (limit = 10) => api.get(`/dashboard/recent-activity?limit=${limit}`),
  getStats: (period) => api.get(`/dashboard/stats?period=${period}`),
  getLowStock: () => api.get('/dashboard/low-stock'),
};

// Tools API
export const toolsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/tools?${params}`);
  },
  getById: (id) => api.get(`/tools/${id}`),
  getByBarcode: (serialNumber) => api.get(`/tools/barcode/${serialNumber}`),
  create: (toolData) => api.post('/tools', toolData),
  update: (id, toolData) => api.put(`/tools/${id}`, toolData),
  retire: (id) => api.delete(`/tools/${id}`),
  getCategories: () => api.get('/tools/categories/list'),
};

// Transactions API
export const transactionsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/transactions?${params}`);
  },
  getById: (id) => api.get(`/transactions/${id}`),
  getOverdue: () => api.get('/transactions/overdue'),
  checkout: (checkoutData) => api.post('/transactions/checkout', checkoutData),
  return: (returnData) => api.post('/transactions/return', returnData),
};

// Employees API
export const employeesAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/employees?${params}`);
  },
  getById: (id) => api.get(`/employees/${id}`),
  create: (employeeData) => api.post('/employees', employeeData),
  update: (id, employeeData) => api.put(`/employees/${id}`, employeeData),
  deactivate: (id) => api.delete(`/employees/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/inventory?${params}`);
  },
  create: (itemData) => api.post('/inventory', itemData),
  update: (id, itemData) => api.put(`/inventory/${id}`, itemData),
};

// Calibrations API
export const calibrationsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/calibrations?${params}`);
  },
  create: (calibrationData) => api.post('/calibrations', calibrationData),
  update: (id, calibrationData) => api.put(`/calibrations/${id}`, calibrationData),
};

// Search API
export const searchAPI = {
  search: (query, type = 'all') => api.get(`/search?q=${encodeURIComponent(query)}&type=${type}`),
};

export default api;
